import React, { FC } from 'react'
import { ReactSVG } from 'react-svg'
import classNames from 'classnames'
import { Link, useParams } from 'react-router-dom'
import { openNewTab, setPath } from '../../../utils/url'
import { URL } from '../../constants/url'
import { LangValue } from '../../../states/types/common'
import { getZendeskToken } from '../../../states/apis/auth'
import { useDispatch, useSelector } from 'react-redux'
import { setErrors } from '../../../states/containers/common/errorsSlice'
import { isAdministrator, isManager } from '../../../utils/role'
import { RootState } from '../../../states/containers/store'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Tips from './Tips'

interface Props {
  openPromotionCallback: VoidFunction
  setTypeCallback: (type: string) => void
}

const SideBar: FC<Props> = (props) => {
  const {
    openPromotionCallback,
    setTypeCallback,
  } = props

  const dispatch = useDispatch()

  const {
    login,
    commonPart,
  } = useSelector((state: RootState) => state)

  const { language } = useParams<{ language: LangValue }>()

  const clickZendesk = () => {
    getZendeskToken()
      .then((result) => {
        openNewTab(result.url, 'post')
      })
      .catch((error) => {
        dispatch(setErrors(error))
      })
  }

  const buildSVG = (isUpgrade: boolean, iconFileName: string) => {
    const filePath = `/static/images/icon/${iconFileName}.svg`
    return (
      <ReactSVG
        src={filePath}
        className={classNames('icon', {
          isUpgrade: isUpgrade,
        })}
        wrapper="span"
      />
    )
  }

  const promotion = (type: string) => {
    if (!type) return
    setTypeCallback(type)
    openPromotionCallback()
  }

  return (
    <>
      <section className="side">
        <Tips
          description="ダッシュボード"
          className=""
          icon={false}
        >
          <Link
            to={setPath(URL.TOP.path, language)}
            className={classNames({
              active: location.pathname.match(URL.TOP.path),
              item: true,
            })}
          >
            <FontAwesomeIcon
              icon={['fas', 'home']}
              className="icon"
            />
            <div className="text">
              HOME
            </div>
          </Link>
        </Tips>
        {
          login.function_enabled_state?.task_management ? (
            <Tips
              description="工数管理"
              className=""
              icon={false}
            >
              <Link
                to={setPath(URL.TASK.path, language)}
                className={classNames({
                  active: location.pathname.match(URL.TASK.path),
                  item: true,
                })}
              >
                <FontAwesomeIcon
                  icon={['fas', 'hammer']}
                  className="icon"
                />
                <div className="text">
                  工数管理
                </div>
              </Link>
            </Tips>
          ) : (
            <>
              {
                (isManager(login.role) || isAdministrator(login.role)) && (
                  <Tips
                    description="工数管理"
                    className=""
                    icon={false}
                  >
                    <div
                      className="lock"
                      onClick={() => promotion("task")}
                      role="none"
                    >
                      <FontAwesomeIcon
                        icon={['fas', 'lock']}
                        className="lock_icon"
                      />
                    </div>
                    <div className="item no_change">
                      <FontAwesomeIcon
                        icon={['fas', 'hammer']}
                        className="icon"
                      />
                      <div className="text">
                        工数管理
                      </div>
                    </div>
                  </Tips>
                )
              }
            </>
          )
        }
        {
          login.function_enabled_state?.task_management ? (
            <Tips
              description="ガントビュー"
              className=""
              icon={false}
            >
              <Link
                to={setPath(URL.GANTT_VIEW.path, language)}
                className={classNames({
                  active: location.pathname.match(URL.GANTT_VIEW.path),
                  item: true,
                })}
              >
                <FontAwesomeIcon
                  icon={['far', 'calendar-alt']}
                  className="icon"
                />
                <div className="text">
                  ガント
                </div>
              </Link>
            </Tips>
          ) : (
            <>
            </>
          )
        }
        {
          login.feature_config?.organizational_analysis_view && (
            <>
              {
                login.function_enabled_state?.organizational_analysis ? (
                  <Tips
                    description="組織分析"
                    className=""
                    icon={false}
                  >
                    <Link
                      to={setPath(URL.ANALYZE.path, language)}
                      className={classNames({
                        active: (location.pathname.match(URL.ANALYZE.path) || location.pathname.match(URL.EXPORT.path)),
                        item: true,
                      })}
                    >
                      {buildSVG(false, "icon-flask")}
                      <div className="text">
                        組織分析
                      </div>
                    </Link>
                  </Tips>
                ) : (
                  <>
                    {
                      (isManager(login.role) || isAdministrator(login.role)) && (
                        <Tips
                          description="組織分析"
                          className=""
                          icon={false}
                        >
                          <div
                            className="lock"
                            onClick={() => promotion("analysis")}
                            role="none"
                          >
                            <FontAwesomeIcon
                              icon={['fas', 'lock']}
                              className="lock_icon"
                            />
                          </div>
                          <div className="item no_change">
                            {buildSVG(false, "icon-flask")}
                            <div className="text">
                              組織分析
                            </div>
                          </div>
                        </Tips>
                      )
                    }
                  </>
                )
              }
            </>
          )
        }
        {
          login.function_enabled_state?.ai_work_assessment && (
            <Tips
              description="AI業務診断"
              className=""
              icon={false}
            >
              <Link
                to={setPath(URL.REPORT.path, language)}
                className={classNames({
                  active: location.pathname.match(URL.REPORT.path),
                  item: true,
                })}
              >
                {buildSVG(false, "icon-report")}
                <div className="text">
                  AI業務診断
                </div>
              </Link>
            </Tips>
          )
        }
        {
          (login.feature_config?.risk_member_report_view || login.feature_config?.risk_team_report_view) && (
            <>
              {
                login.function_enabled_state?.risk_assessment ? (
                  <Tips
                    description="リスクアセスメント"
                    className=""
                    icon={false}
                  >
                    <Link
                      to={setPath(URL.RISK_TIMELINE.path, language)}
                      className={classNames({
                        active: location.pathname.match(URL.RISK.path),
                        item: true,
                      })}
                    >
                      <FontAwesomeIcon
                        icon={['fas', 'heartbeat']}
                        className="icon"
                      />
                      <div className="text">
                        リスク
                      </div>
                    </Link>
                  </Tips>
                ) : (
                  <>
                    {
                      !isAdministrator(login.role) && (
                        <Tips
                          description="リスクアセスメント"
                          className=""
                          icon={false}
                        >
                          <div
                            className="lock"
                            onClick={() => promotion("risk")}
                            role="none"
                          >
                            <FontAwesomeIcon
                              icon={['fas', 'lock']}
                              className="lock_icon"
                            />
                          </div>
                          <div className="item no_change">
                            <FontAwesomeIcon
                              icon={['fas', 'heartbeat']}
                              className="icon"
                            />
                            <div className="text">
                              リスク
                            </div>
                          </div>
                        </Tips>
                      )
                    }
                  </>
                )
              }
            </>
          )
        }
        {
          !login.function_enabled_state?.risk_assessment && isAdministrator(login.role) && (
            <>
              <Tips
                description="リスクアセスメント"
                className=""
                icon={false}
              >
                <div
                  className="lock"
                  onClick={() => promotion("risk")}
                  role="none"
                >
                  <FontAwesomeIcon
                    icon={['fas', 'lock']}
                    className="lock_icon"
                  />
                </div>
                <div className="item no_change">
                  <FontAwesomeIcon
                    icon={['fas', 'heartbeat']}
                    className="icon"
                  />
                  <div className="text">
                    リスク
                  </div>
                </div>
              </Tips>
            </>
          )
        }
        {
          login.function_enabled_state?.self_management ? (
            <Tips
              description="セルフマネジメント"
              className=""
              icon={false}
            >
              <Link
                to={setPath(URL.DATA_SUMMARY.path, language)}
                className={classNames({
                  active: location.pathname.match(URL.DATA_SUMMARY.path),
                  item: true,
                })}
              >
                <FontAwesomeIcon
                  icon={['fas', 'child']}
                  className="icon"
                />
                <div className="text">
                  セルフ
                </div>
              </Link>
            </Tips>
          ) : (
            <>
              {
                (isManager(login.role) || isAdministrator(login.role)) && (
                  <Tips
                    description="セルフマネジメント"
                    className=""
                    icon={false}
                  >
                    <div
                      className="lock"
                      onClick={() => promotion("self")}
                      role="none"
                    >
                      <FontAwesomeIcon
                        icon={['fas', 'lock']}
                        className="lock_icon"
                      />
                    </div>
                    <div className="item no_change">
                      <FontAwesomeIcon
                        icon={['fas', 'child']}
                        className="icon"
                      />
                      <div className="text">
                        セルフ
                      </div>
                    </div>
                  </Tips>
                )
              }
            </>
          )
        }
        <Tips
          description="設定"
          className=""
          icon={false}
        >
          <Link
            to={setPath(URL.SETTING_PERSONAL.path, language)}
            className={classNames({
              active: location.pathname.match(URL.SETTING.path),
              item: true,
            })}
          >
            {
              isAdministrator(commonPart.role) && !commonPart.teams?.length && (
                <div className="attention" />
              )
            }
            {buildSVG(false, "icon-setting")}
            <div className="text">
              設定
            </div>
          </Link>
        </Tips>
        <Tips
          description="ヘルプ"
          className=""
          icon={false}
          external
        >
          <label htmlFor="#" onClick={clickZendesk} role="none" className="item">
            {buildSVG(false, "icon-help")}
            <div className="text">
              ヘルプ
              <FontAwesomeIcon
                icon={['fas', 'external-link-alt']}
                pull="right"
                className="external"
              />
            </div>
          </label>
        </Tips>
      </section>
    </>
  )
}

export default SideBar
